import { styled } from '@mui/material/styles';
import { ListItemText, Button, Typography, Card, CardContent, TextField, Checkbox, Switch, Select, MenuItem, Snackbar, Box, Link } from '@mui/material';
import { Accordion, Grid, AccordionSummary, AccordionDetails, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';


const responsiveFontSize = (fontSize, theme) => ({
  // Extra-small screens (default)
  '@media (max-width: 479px)': {
    fontSize: fontSize?.xs || `calc(${theme.typography.body1.fontSize} - 0.25rem)`, // Slightly smaller than body1
  },
  // Small screens (≥480px)
  '@media (min-width: 480px)': {
    fontSize: fontSize?.sm || `calc(${theme.typography.body1.fontSize} - 0.2rem)`, // Smaller than body1
  },
  // Medium screens (≥600px)
  '@media (min-width: 600px)': {
    fontSize: fontSize?.md || `calc(${theme.typography.body1.fontSize} - 0.15rem)`, // Scaled down body1
  },
  // Large screens (≥960px)
  '@media (min-width: 960px)': {
    fontSize: fontSize?.lg || `calc(${theme.typography.body1.fontSize} - 0.1rem)`, // Slightly smaller on large screens
  },
  // Extra-large screens (≥1280px)
  '@media (min-width: 1280px)': {
    fontSize: fontSize?.xl || theme.typography.body1.fontSize, // Standard body1 size for larger screens
  },
});



// Styled ListItemText
export const StyledListItemText = styled(ListItemText)`
  .MuiListItemText-primary {
    color: ${(props) => props.theme?.palette?.primary?.main};
    ${props => responsiveFontSize(props.fontSize, props.theme)}
  }
  .MuiListItemText-secondary {
    color: ${(props) => props.theme?.palette?.secondary?.main};
    ${props => responsiveFontSize(props.fontSize, props.theme)}
  }
`;

// Styled Button
export const StyledButton = styled(Button)` 
  background-color: ${props => props.primaryColor || props.theme.palette.buttonColor.main}; // Use your custom button color
  color: ${props => props.theme.palette.buttonColor.contrastText}; // Text color from the custom color
    ${props => responsiveFontSize(props.fontSize, props.theme)}
   text-transform: none; // Prevent text from transforming to uppercase

  &:hover {
    background-color: ${props => props.theme.palette.buttonColor.dark}; // Darker shade on hover
  }

  // Responsive padding based on screen size
  padding: 6px 12px;  // Default padding for larger screens

  @media (max-width: 600px) {
    padding: 4px 8px; // Smaller padding for small screens
  }

  @media (min-width: 601px) and (max-width: 960px) {
    padding: 5px 10px; // Default padding for medium screens
  }

  @media (min-width: 961px) {
    padding: 6px 12px; // Larger padding for large screens
  }
`;
// Styled Typography
export const StyledTypography = styled(Typography)`
  ${props => responsiveFontSize(props.fontSize, props.theme)}
  color: ${props => props.theme?.palette?.text?.primary || '#333'}; 
`;


// Styled TextField
export const StyledTextField = styled(TextField)`
  .MuiInputBase-input {
    color: ${props => props.theme?.palette?.text?.primary || '#333'};
${props => responsiveFontSize(props.fontSize, props.theme)}  }
  .MuiFormLabel-root {
    color: ${props => props.theme?.palette?.text?.secondary || '#6c757d'};
  }
  .MuiOutlinedInput-root {
    &.Mui-focused fieldset {
      border-color: ${props => props.theme?.palette?.primary?.main || '#007bff'};
    }
  }
`;

// Styled Checkbox
export const StyledCheckbox = styled(Checkbox)`
  .MuiCheckbox-root {
    color: ${props => props.theme?.palette?.primary?.main || '#007bff'};
    &.Mui-checked {
      color: ${props => props.theme?.palette?.primary?.dark || '#0056b3'};
    }
  }
`;

// Styled Switch
export const StyledSwitch = styled(Switch)`
  .MuiSwitch-thumb {
    background-color: ${props => props.theme?.palette?.primary?.main || '#007bff'};
  }
  .MuiSwitch-track {
    background-color: ${props => props.theme?.palette?.background?.default || '#f5f5f5'};
  }
`;

// Styled Select
export const StyledSelect = styled(Select)`
  .MuiSelect-select {
    color: ${props => props.theme?.palette?.text?.primary || '#333'};
    ${props => responsiveFontSize(props.fontSize, props.theme)}
  }
  .MuiOutlinedInput-root {
    &.Mui-focused fieldset {
      border-color: ${props => props.theme?.palette?.primary?.main || '#007bff'};
    }
  }
`;

// Styled MenuItem
export const StyledMenuItem = styled(MenuItem)`
  font-size: ${props => props.fontSize || '16px'};
  color: ${props => props.theme?.palette?.text?.primary || '#333'};
`;

// Styled Snackbar
export const StyledSnackbar = styled(Snackbar)`
  .MuiSnackbarContent-root {
    background-color: ${props => props.theme?.palette?.error?.main || '#f44336'};
    color: ${props => props.theme?.palette?.background?.default || '#fff'};
  }
`;


export const StyledAccordion = styled(Accordion)`
  background-color: ${props => props.theme?.palette?.background?.default || '#f5f5f5'};
  margin-bottom: ${props => props.marginBottom || '4px'};

  &.Mui-expanded {
    margin: ${props => props.marginExpanded || '4px 0'};
  }
`;

export const StyledAccordionSummary = styled(AccordionSummary)`
  background-color: transparent;
  min-height: 40px;  
  &.Mui-expanded {
    min-height: 40px;  /* Keep consistent height when expanded */
  }

  .MuiAccordionSummary-content {
    margin: 0;          /* Remove extra margin */
    align-items: center; /* Vertically align content */
    padding: 0;         /* Remove inner padding for a tighter look */
  }
`;

export const StyledAccordionDetails = styled(AccordionDetails)`
  background-color: ${props => props.theme?.palette?.background?.default || '#f5f5f5'};
  color: ${props => props.theme?.palette?.text?.primary || '#333'};
  padding: ${props => props.padding || '8px 16px'}; /* Reduced padding for a compact layout */
`;


// StyledDialog
export const StyledDialog = styled(Dialog)`
  .MuiPaper-root {
    background-color: ${(props) => props.theme?.palette?.background?.default || "#fff"};
    padding: ${(props) => props.padding || "16px"};
    border-radius: ${(props) => props.borderRadius || "8px"};
  }
`;

// StyledDialogTitle
export const StyledDialogTitle = styled(DialogTitle)`
  background-color: ${(props) => props.theme?.palette?.primary?.main || "#007bff"};
  color: ${(props) => props.theme?.palette?.background?.default || "#fff"};
  padding: ${(props) => props.padding || "16px"};
  ${props => responsiveFontSize(props.fontSize, props.theme)}
  font-weight: ${(props) => props.fontWeight || "bold"};
`;

// StyledDialogContent
export const StyledDialogContent = styled(DialogContent)`
background-color: ${(props) => props.theme?.palette?.background?.default || "#f5f5f5"};
  padding: ${(props) => props.padding || "16px"};
`;

// StyledDialogContentText
export const StyledDialogContentText = styled(DialogContentText)`
  color: ${(props) => props.theme?.palette?.text?.primary || "#000"};
  ${props => responsiveFontSize(props.fontSize, props.theme)}
  line-height: ${(props) => props.lineHeight || "1.5"};
  margin-bottom: ${(props) => props.marginBottom || "16px"};
`;

// StyledDialogActions
export const StyledDialogActions = styled(DialogActions)`
  background-color: ${(props) => props.theme?.palette?.background?.default || "#f5f5f5"};
  padding: ${(props) => props.padding || "8px 24px"};
  display: flex;
  justify-content: ${(props) => props.justifyContent || "flex-end"};
  gap: ${(props) => props.gap || "8px"};

  & .MuiButton-root {
    min-width: ${(props) => props.buttonMinWidth || "100px"};
  }
`;


export const StyledCard = styled(Card)`
  background-color: ${(props) => props.theme?.palette?.background?.default || "#fff"};
  border-radius: ${(props) => props.borderRadius || "8px"};
  box-shadow: ${(props) => props.boxShadow || "0px 4px 10px rgba(0, 0, 0, 0.1)"};
  padding: ${(props) => props.padding || "16px"};
  transition: 0.3s;

  &:hover {
    box-shadow: ${(props) => props.hoverBoxShadow || "0px 6px 15px rgba(0, 0, 0, 0.2)"};
  }
`;

// StyledCardContent
export const StyledCardContent = styled(CardContent)`
  padding: ${(props) => props.padding || "16px"};
  color: ${(props) => props.theme?.palette?.text?.primary || "#000"};
  background-color: ${(props) => {
    return props.backgroundColor || props.theme?.palette?.background?.default || "#f5f5f5"
  }};
font - size: ${(props) => responsiveFontSize(props.fontSize, props.theme)}
line - height: ${(props) => props.lineHeight || "1.5"};
`;
// Styled Grid with proper typing
export const StyledGrid = styled(Grid)(({ theme }) => ({
  backgroundColor: theme?.palette?.background?.default || '#f5f5f5',
  color: theme?.palette?.text?.primary || '#333',
  padding: '1px',
}));

export const StyledLink = styled(Link)(({ theme, fontSize }) => ({
  textDecoration: 'none',
  color: theme.palette.primary.contrastText,
  padding: theme.spacing(1, 2),
  borderRadius: 9,
  ...responsiveFontSize(fontSize, theme), // Use the responsiveFontSize function here
  fontWeight: theme.typography.fontWeightRegular,
  fontFamily: theme.typography.fontFamily,
  textAlign: 'center',
  border: 'none',
  cursor: 'pointer',
  display: 'inline-block',
  transition: 'background-color 0.3s ease',
}));

export const StyledBox = styled(Box)(({ theme }) => ({
  borderRadius: 12, // Adjusted border radius
  backgroundColor: theme.palette.background.default, // Default background color
  color: theme.palette.text.primary, // Default text color 
  transition: 'background-color 0.3s ease',
}));


